import { AxiosError } from "axios";

import { useQueryClient } from "@enkor/react-query";
import { useBooleanState } from "@enkor/toss-slash";

import useModalState from "@logics/hooks/useModalState";
import { ToastError, ToastSuccess } from "@logics/utils/toast";
import { ERROR_MESSAGE } from "@domains/common/constants/toastMessages";
import { PROJECT_MESSAGE } from "@domains/project/constants/toastMessages";
import { projectQueryKeys } from "@domains/project/constants/projectQueryKeys";
import { ProjectInfoTrackingDataType } from "@domains/project/types/project";
import {
  useCreateEventRegisterMutation,
  useCreateProjectRegisterMutation,
} from "@domains/project/network/projectMutations";
import WebViewBridge from "@logics/utils/webViewBridge";
import { useAuthSession } from "@domains/auth/hooks/useAuthSession";
import { isIOSAndMac } from "@logics/utils/isMobile";
import { ErrorCodeType } from "@domains/common/types/common";
import { ErrorResponseType } from "@models/client";
import { APP_STORE_LINK } from "@domains/common/constants/deepLinks";
import { eventQueryKeys } from "@domains/event/constants/eventQueryKeys";
import { myPageQueryKeys } from "@domains/myPage/constants/myPageQueryKeys";
import { useFetchProjectEventListForChatQuery } from "@domains/project/network/projectQueries";

import BaseButtonGroup from "../BaseButtonGroup/BaseButtonGroup";

interface ParticipateButtonGroupProps {
  projectId: string;
  projectInfo: ProjectInfoTrackingDataType;
}

const ParticipateButtonGroup = ({
  projectId,
  projectInfo,
}: ParticipateButtonGroupProps) => {
  const queryClient = useQueryClient();

  const { closeModal } = useModalState();

  const { user, guardRoute } = useAuthSession();

  const [isProcessingPayment, startProcessingPayment, finishProcessingPayment] =
    useBooleanState();

  const { refetch: refetchProjectEventList } =
    useFetchProjectEventListForChatQuery(projectId);

  const registerMutation = useCreateProjectRegisterMutation();
  const createEventRegisterMutation = useCreateEventRegisterMutation();

  const handleJoinError = (errorCode?: ErrorCodeType) => {
    switch (errorCode) {
      default:
        ToastError(ERROR_MESSAGE.SERVER_ERROR);
        break;
    }
  };

  const handleClickPaymentInWeb = () => {
    if (process.env.NEXT_PUBLIC_NODE_ENV !== "production") {
      guardRoute(async () => {
        if (registerMutation.isPending) return;
        try {
          await registerMutation.mutateAsync(projectId);
          queryClient.invalidateQueries({
            queryKey: projectQueryKeys.getProjectDetail({ projectId }),
          });

          ToastSuccess(PROJECT_MESSAGE.JOIN_COMPLETED);
        } catch (error) {
          const { response } = error as AxiosError<ErrorResponseType>;
          handleJoinError(response?.data?.errorCode);
        }
      });
      return;
    }

    if (isIOSAndMac()) {
      window.open(APP_STORE_LINK.IOS, "_blank");
    } else {
      window.open(APP_STORE_LINK.ANDROID, "_blank");
    }
  };

  const handleClickPayment = () => {
    guardRoute(async () => {
      if (isProcessingPayment) return;

      try {
        startProcessingPayment();

        const result = await WebViewBridge.getInstance().triggerPayProject(
          projectInfo,
          user?.hasProfile,
        );

        if (result === "success") {
          queryClient.invalidateQueries({
            queryKey: projectQueryKeys.getProjectDetail({ projectId }),
          });

          // NOTE: 에디션에 참여한 프로젝트에 대해서 이벤트 참여 API Call
          const eventList = await refetchProjectEventList(); // NOTE: 프로젝트 이벤트 목록 리패치 (해당 프로젝트의 이벤트를 보장)
          const firstEvent = eventList?.data?.[0];
          if (firstEvent) {
            const eventId = String(firstEvent.id);
            await createEventRegisterMutation.mutateAsync(eventId);

            closeModal();

            queryClient.invalidateQueries({
              queryKey: eventQueryKeys.getEventDetail({ eventId }),
            });

            queryClient.invalidateQueries({
              queryKey: myPageQueryKeys.getEventList({ participating: true }),
            });
          } else {
            ToastError(PROJECT_MESSAGE.EVENT_JOIN_ERROR);
          }
        }
      } catch (error) {
        const { response } = error as AxiosError<ErrorResponseType>;

        handleJoinError(response?.data?.errorCode);
      } finally {
        finishProcessingPayment();
      }
    });
  };

  const getButtonText = () => {
    if (registerMutation.isPending || isProcessingPayment) {
      return undefined; // BaseButtonGroup에서 LoadingSpinner를 보여줌
    }

    if (
      WebViewBridge.isInAppWebView() ||
      process.env.NEXT_PUBLIC_NODE_ENV !== "production"
    ) {
      return "Payment and join";
    }
    return "View in Enkoplay app";
  };

  return (
    <BaseButtonGroup
      actionButtonProps={{
        onClick: WebViewBridge.isInAppWebView()
          ? handleClickPayment
          : handleClickPaymentInWeb,
        disabled: registerMutation.isPending || isProcessingPayment,
      }}
      buttonContent={getButtonText()}
      projectInfo={projectInfo}
    />
  );
};

export default ParticipateButtonGroup;
